<template>
    <v-row justify="center" align="center">
        <v-col cols="12" sm="12" md="12">
            <import-page-banner
                title="Payment"
                sample-csv-path="/createPaymentByImportSample.csv"
            />

            <v-card>
                <v-card-title>
                    <v-row>
                        <v-col cols="12" sm="12" md="6"> Create Payment of Marketplace Order by Import </v-col>
                        <v-col cols="12" sm="12" md="6"> </v-col>
                    </v-row>
                </v-card-title>
                <v-card-text>
                    <p><b>Only accept csv file.</b></p>
                    <p>1. Choose your file</p>
                    <p>2. SELECT THE CORRECT MAPPING OF Field and Column</p>
                    <p>3. IMPORT</p>
                    <p>
                        <master-data-reference-button
                            button-label="Marketplace List"
                            dialog-title="Valid Marketplace Codes"
                            :items="marketplaceOptions"
                        />
                        <master-data-reference-button
                            button-label="Currency List"
                            dialog-title="Valid Currency Codes"
                            :items="currencyOptions"
                        />
                        <master-data-reference-button
                            button-label="Payment Type List"
                            dialog-title="Valid Payment Types"
                            :items="paymentTypeList"
                        />
                    </p>

                    <!-- New CSV Importer Component -->
                    <csv-importer
                        :field-definitions="data.fields"
                        :preview-headers="data.previewHeaders"
                        @data-loaded="onCsvDataLoaded"
                        @data-cleared="onCsvDataCleared"
                    />
                </v-card-text>
            </v-card>
            <br />
            <v-card v-if="data.mappedCsv && data.mappedCsv.length > 0">
                <v-card-title>
                    <v-row>
                        <v-col cols="12" sm="12" md="12">
                            <v-btn
                                class="mr-2"
                                color="primary"
                                :loading="loading"
                                :disabled="loading || invalidFile"
                                @click="onImportClick"
                            >
                                <v-icon>mdi-cloud-upload</v-icon>
                                Import
                            </v-btn>
                            <v-btn class="mr-2" :to="pathListPayment"> Back to list </v-btn>
                            <span v-if="importDone">{{ importDoneMsg }} </span>
                        </v-col>
                    </v-row>
                </v-card-title>
                <v-card-text>
                    <v-table>
                        <thead>
                            <tr>
                                <th class="text-left" v-for="header in data.headers" :key="header.value">
                                    {{ header.text }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(item, idx) in transformMappedCsv(data.mappedCsv)"
                                :key="`row${idx}`"
                                :class="invalidRowClass(item)"
                            >
                                <td v-for="header in data.headers" :key="header.value">
                                    {{ item[header.value] }}
                                </td>
                            </tr>
                        </tbody>
                    </v-table>
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>
</template>

<script lang="ts">
import { defineComponent, reactive, onMounted } from 'vue';
import { fetchWrapper } from '../../utils/FetchWrapper';
import { PaymentLabelTextHelper } from '../../utils/labelTextHelper/PaymentLabelTextHelper';
import * as _ from 'lodash';
import { PathDefinition } from '../../utils/PathDefinition';
import { ImportTableLabelTextHelper } from '../../utils/labelTextHelper/ImportTableLabelTextHelper';
import { SharedMsgHelper } from '../../utils/SharedMsgHelper';
import { ValidationHelper } from '../../utils/ValidationHelper';
import { Formatter } from '../../utils/Formatter';
import { MasterDataApiCaller } from '../../utils/MasterDataApiCaller';
import { PaymentHeaderHelper } from '../../utils/headerHelper/PaymentHeaderHelper';
import { ImportHeaderHelper } from '../../utils/ImportHeaderHelper';
import { useMasterData } from '../../composables/useMasterData';
import MasterDataReferenceButton from '../../components/MasterDataReferenceButton.vue';
import CsvImporter from '../../components/CsvImporter.vue';
import ImportPageBanner from '../../components/ImportPageBanner.vue';
import { PaymentImportValidation } from '../../config/importValidationConfigs';
import { ImportValidationHelper } from '../../utils/ImportValidationHelper';

export default defineComponent({
    name: 'ImportPayment',

    components: {
        MasterDataReferenceButton,
        CsvImporter,
        ImportPageBanner
    },

    setup() {
        // Load master data options for reference
        const { marketplaceOptions, currencyOptions, loadAllMasterData } = useMasterData();

        // Payment type enum values for reference
        const paymentTypeList = [
            { code: 'ORDER_PAYMENT_BUYER' },
            { code: 'COMMISSION_PAYMENT_SELLER' },
            { code: 'TRANSACTION_FEE_PAYMENT_SELLER' },
            { code: 'PRODUCT_COST_PAYMENT_SELLER' },
            { code: 'SHIPPING_FEE_PAYMENT_SELLER' },
            { code: 'OTHER_PAYMENT_SELLER' },
            { code: 'OTHER_PAYMENT_BUYER' }
        ];

        const data = reactive({
            mappedCsv: null,
            fields: PaymentHeaderHelper.getImportFields(),
            previewHeaders: PaymentHeaderHelper.getImportHeaders(),
            headers: [...ImportHeaderHelper.getHeaders(), ...PaymentHeaderHelper.getImportHeaders()]
        });

        onMounted(async () => {
            // Load master data for reference button
            await loadAllMasterData();
        });

        return {
            data,
            marketplaceOptions,
            currencyOptions,
            paymentTypeList
        };
    },
    data() {
        return {
            loading: false,
            invalidFile: false,
            importDone: false,
            importDoneMsg: SharedMsgHelper.getImportDoneMsg,
            pathListPayment: PathDefinition.listPayment,
            formLabelText: PaymentLabelTextHelper.getLabelText
        };
    },
    async created() {},
    methods: {
        onCsvDataLoaded(mappedData) {
            // Receive data from CSV importer component
            this.data.mappedCsv = mappedData;
        },
        onCsvDataCleared() {
            // Clear data when file is removed
            this.data.mappedCsv = null;
            this.importDone = false;
            this.invalidFile = false;
        },
        transformMappedCsv(mappedCsv) {
            const importHeaderValues = this.data.headers.map((header) => header.value);
            return ImportValidationHelper.validateRows(mappedCsv, PaymentImportValidation, importHeaderValues);
        },

        groupDataByOrderKey(mappedCsv) {
            const orderFields = ['orderNumber', 'marketplaceCode'];
            const paymentItemFields = [
                'paymentType',
                'paymentAmount',
                'paymentCurrencyCode',
                'datePaymentOn',
                'description',
                'remarks'
            ];

            // Create composite key: orderNumber + marketplaceCode
            const groupByCompositeKey = (item) => `${item.orderNumber}|${item.marketplaceCode}`;

            return _.mapValues(_.groupBy(mappedCsv, groupByCompositeKey), (group) => {
                const orderInfo = _.pick(group[0], [...orderFields, 'status', 'error']);
                const paymentItems = group.map((item) => _.pick(item, [...paymentItemFields, 'status', 'error']));

                return {
                    orderInfo,
                    paymentItems,
                    orderNumber: group[0].orderNumber,
                    marketplaceCode: group[0].marketplaceCode,
                    compositeKey: groupByCompositeKey(group[0])
                };
            });
        },

        validateGroupConsistency(groupedData) {
            const orderFields = ['orderNumber', 'marketplaceCode'];

            for (const compositeKey in groupedData) {
                const group = groupedData[compositeKey];
                const originalData = this.data.mappedCsv.filter(
                    (item) => item.orderNumber === group.orderNumber && item.marketplaceCode === group.marketplaceCode
                );

                // Check if orderNumber and marketplaceCode are consistent across rows
                for (let i = 1; i < originalData.length; i++) {
                    for (const field of orderFields) {
                        if (originalData[0][field] !== originalData[i][field]) {
                            originalData[i].status = ImportTableLabelTextHelper.getImportResultValue.error;
                            originalData[i].error = `Inconsistent ${field} for order group`;
                        }
                    }
                }
            }
        },
        async onImportClick() {
            this.data.mappedCsv = this.transformMappedCsv(this.data.mappedCsv);
            if (this.data.mappedCsv && this.data.mappedCsv.length > 0) {
                await this.importSubmit();
            } else {
                console.log('empty csv');
            }
        },
        async importSubmit() {
            this.loading = true;

            // Group data by orderNumber + marketplaceCode and validate consistency
            const groupedData = this.groupDataByOrderKey(this.data.mappedCsv);
            this.validateGroupConsistency(groupedData);

            const isValid = this.validation(this.data.mappedCsv);
            if (isValid) {
                // Process each order group
                for (const compositeKey in groupedData) {
                    await this.createPaymentGroup(groupedData[compositeKey]);
                }
                this.importDone = true;
                // prevent import again
                this.invalidFile = true;
            } else {
                console.log('invalid csv');
            }
            this.loading = false;
        },
        validation(list) {
            return _.every(list, (v) => {
                return !this.isInvalidRow(v);
            });
        },
        invalidRowClass(item) {
            if (this.isInvalidRow(item)) {
                return 'row-validation-error';
            }
            if (item.status === ImportTableLabelTextHelper.getImportResultValue.skipped) {
                return 'row-validation-skipped';
            }
            return '';
        },
        isInvalidRow(item) {
            return (
                this.isInvalidRowContent(item) || item.status === ImportTableLabelTextHelper.getImportResultValue.error
            );
        },
        isInvalidRowContent(item) {
            return ImportValidationHelper.hasValidationErrors(item, PaymentImportValidation);
        },
        async createPaymentGroup(paymentGroup) {
            const { orderNumber, marketplaceCode } = paymentGroup;

            try {
                const groupRows = this.data.mappedCsv.filter(
                    (item) => item.orderNumber === orderNumber && item.marketplaceCode === marketplaceCode
                );

                const paymentItems = groupRows.map((item) => {
                    if (!item.importRowId) {
                        item.importRowId = crypto.randomUUID();
                    }

                    return {
                        importRowId: item.importRowId,
                        paymentType: item.paymentType,
                        paymentAmount: parseFloat(item.paymentAmount),
                        paymentCurrencyCode: item.paymentCurrencyCode,
                        datePaymentOn: item.datePaymentOn,
                        description: item.description,
                        remarks: item.remarks || ''
                    };
                });

                const request = {
                    orderNumber: orderNumber,
                    marketplaceCode: marketplaceCode,
                    paymentItems
                };

                const requestUrl = `/api/staff/payment/create`;
                const response = await fetchWrapper.post(requestUrl, request);

                if (response.success) {
                    const skippedByRowId = new Map(
                        (response.result?.skipped || []).map((skippedItem) => [
                            skippedItem.importRowId,
                            skippedItem.reason
                        ])
                    );

                    for (const row of groupRows) {
                        if (skippedByRowId.has(row.importRowId)) {
                            row.status = ImportTableLabelTextHelper.getImportResultValue.skipped;
                            row.error = skippedByRowId.get(row.importRowId);
                        } else {
                            row.status = ImportTableLabelTextHelper.getImportResultValue.success;
                            row.error = null;
                        }
                    }
                } else {
                    this.updatePaymentRowsStatus(
                        orderNumber,
                        marketplaceCode,
                        ImportTableLabelTextHelper.getImportResultValue.error,
                        response.errors
                    );
                }
            } catch (error) {
                console.error('Error creating payment group:', error);
                this.updatePaymentRowsStatus(
                    orderNumber,
                    marketplaceCode,
                    ImportTableLabelTextHelper.getImportResultValue.error,
                    error.message || 'Unknown error'
                );
            }
        },

        updatePaymentRowsStatus(orderNumber, marketplaceCode, status, error) {
            for (let i = 0; i < this.data.mappedCsv.length; i++) {
                if (
                    this.data.mappedCsv[i].orderNumber === orderNumber &&
                    this.data.mappedCsv[i].marketplaceCode === marketplaceCode
                ) {
                    this.data.mappedCsv[i].status = status;
                    if (error) {
                        this.data.mappedCsv[i].error = error;
                    }
                }
            }
        }
    }
});
</script>

<style>
select {
    border: 1px solid !important;
}

.row-validation-error {
    background: red;
}

.row-validation-skipped {
    background: #fff3cd;
}
</style>
